import { TaxTypeDatableDocument } from 'generated/resources/datatable.graphql'
import {
  CreateFinanceTaxRateDocument,
  CreateFinanceTaxRateMutationVariables,
  CreateFinanceTaxTypeDocument,
  CreateFinanceTaxTypeMutationVariables,
  DeleteFinanceTaxRateDocument,
  DeleteFinanceTaxTypeDocument,
  EditFinanceTaxRateDocument,
  EditFinanceTaxTypeDocument,
  FinanceValueObjectsTaxTypeEnum,
  GetLegalEntityFinanceTaxTypesDocument,
  TaxRateFragment,
  TaxTypeFragment,
  UpdateFinanceTaxTypeLedgerAccountAssignmentsInput,
} from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'

export const useTaxTypes = (ids?: string[]) => {
  const { data, loading, error } = useQuery(GetLegalEntityFinanceTaxTypesDocument, {
    variables: { ids: ids },
  })

  const [createFinanceTaxType] = useMutation(CreateFinanceTaxTypeDocument, {
    extractorFn: (data) => data.financeMutations.createFinanceTaxType,
  })
  const [deleteFinanceTaxType] = useMutation(DeleteFinanceTaxTypeDocument, {
    extractorFn: (data) => data.financeMutations.deleteFinanceTaxType,
  })

  const [editFinanceTaxType] = useMutation(EditFinanceTaxTypeDocument, {
    extractorFn: (data) => data.financeMutations.updateFinanceTaxType,
  })

  const [createFinanceTaxRate] = useMutation(CreateFinanceTaxRateDocument, {
    extractorFn: (data) => data.financeMutations.createFinanceTaxRate,
  })

  const [editFinanceTaxRate] = useMutation(EditFinanceTaxRateDocument, {
    extractorFn: (data) => data.financeMutations.updateFinanceTaxRate,
  })

  const [deleteFinanceTaxRate] = useMutation(DeleteFinanceTaxRateDocument, {
    extractorFn: (data) => data.financeMutations.deleteFinanceTaxRate,
  })

  const createTaxType = (variables: CreateFinanceTaxTypeMutationVariables) => {
    return createFinanceTaxType({
      variables,
      refetchQueries: [TaxTypeDatableDocument],
    })
  }

  const createTaxRate = (variables: CreateFinanceTaxRateMutationVariables) => {
    return createFinanceTaxRate({
      variables,
      refetchQueries: [GetLegalEntityFinanceTaxTypesDocument],
    })
  }

  const deleteTaxType = async (id: string) => {
    return await deleteFinanceTaxType({
      variables: {
        id: id,
      },
      refetchQueries: [TaxTypeDatableDocument],
    })
  }

  const editTaxType = async (
    id: string,
    name: string,
    type: FinanceValueObjectsTaxTypeEnum,
    ledgerAccountAssignments?: UpdateFinanceTaxTypeLedgerAccountAssignmentsInput[],
    countryCode?: string
  ) => {
    return await editFinanceTaxType({
      variables: {
        id,
        name,
        ledgerAccountAssignments,
        type,
        countryCode,
      },
      refetchQueries: [TaxTypeDatableDocument],
    })
  }

  const editTaxRate = async (taxTypeId: string, taxRateId: string, description: string) => {
    return await editFinanceTaxRate({
      variables: {
        id: taxRateId,
        description,
      },
      refetchQueries: [GetLegalEntityFinanceTaxTypesDocument],
    })
  }

  const deleteTaxRate = async (taxTypeId: string, taxRateId: string) => {
    return await deleteFinanceTaxRate({
      variables: {
        id: taxRateId,
      },
      refetchQueries: [GetLegalEntityFinanceTaxTypesDocument],
    })
  }

  return {
    data,
    loading,
    error,
    createTaxType,
    deleteTaxType,
    editTaxType,
    createTaxRate,
    editTaxRate,
    deleteTaxRate,
  }
}

export type { TaxTypeFragment, TaxRateFragment }
